// Shown while a job runs — a stepper for where the solve is, and an elapsed
// timer against the engine's budget for how long it may still run.
//
// Deliberately no overall percentage. The outer loop repeats until it
// converges or the budget expires, so the total is unknowable at the start:
// a run finishing in pass 1 at 60s of a 900s budget is 7% of the clock and
// 25% of the ladder, and both would then jump straight to done. A bar that
// lives near zero and teleports teaches the operator to ignore it. Bars are
// drawn only inside a stage, over a fraction the engine actually reported.
//
// Stage names are rendered here, in the reader's language. The backend's
// progress sentence is English-only and stays available as the fine print.

import { useEffect, useState } from "react";

import { useT } from "../lib/i18n";
import type { JobStage } from "../types";

const STAGES = ["distances", "assign", "route", "finalize"] as const;

const STAGE_LABEL = {
  distances: "progress.stageDistances",
  assign: "progress.stageAssign",
  route: "progress.stageRoute",
  finalize: "progress.stageFinalize",
} as const;

function clock(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = String(Math.floor(seconds % 60)).padStart(2, "0");
  return `${m}:${s}`;
}

export function ProgressView({
  message,
  elapsed,
  budget,
  stage,
}: {
  message: string;
  elapsed?: number;
  budget?: number;
  stage?: JobStage | null;
}) {
  const t = useT();
  const [localElapsed, setLocalElapsed] = useState(0);
  useEffect(() => {
    const t0 = Date.now();
    const id = setInterval(() => setLocalElapsed(Math.floor((Date.now() - t0) / 1000)), 1000);
    return () => clearInterval(id);
  }, []);

  // Server elapsed only refreshes on each poll (2s), so keep ticking between
  // them: whichever clock is further along is the one that hasn't stalled.
  const shown = Math.max(localElapsed, elapsed ?? 0);
  const current = stage ? STAGES.indexOf(stage.stage) : -1;

  return (
    <section className="progress">
      <div className="progress-card">
        <div className="spinner" />
        <h2>{t("progress.building")}</h2>

        {current >= 0 && (
          <ol className="progress-steps">
            {STAGES.map((name, i) => {
              const state = i < current ? "done" : i === current ? "active" : "todo";
              const active = state === "active";
              // A re-plan sends the solve back to assigning. Say so, rather
              // than let the stepper appear to slide backwards for no reason.
              const retry =
                active && stage && stage.attempt && stage.attempt > 1
                  ? t("progress.retry", { n: stage.attempt })
                  : null;
              return (
                <li key={name} className={`progress-step is-${state}`}>
                  <span className="progress-step-label">{t(STAGE_LABEL[name])}</span>
                  {retry && <span className="progress-step-note">{retry}</span>}
                  {active && stage?.total ? (
                    <span className="progress-step-count">
                      {stage.done ?? 0} / {stage.total}
                    </span>
                  ) : null}
                  {active && stage?.total ? (
                    <span className="progress-bar">
                      <span
                        className="progress-bar-fill"
                        style={{ width: `${((stage.done ?? 0) / stage.total) * 100}%` }}
                      />
                    </span>
                  ) : null}
                </li>
              );
            })}
          </ol>
        )}

        <div className="progress-msg">{message}</div>
        <div className="progress-elapsed">
          {clock(shown)} {t("progress.elapsed")}
          {budget ? ` ${t("progress.ofBudget", { budget: clock(budget) })}` : ""}
        </div>
        <p className="progress-hint">{t("progress.hint")}</p>
      </div>
    </section>
  );
}
